import { FC, useCallback, useEffect, useState } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import CTAButton from '@/components/ui/CTAButton';
import api from '@/services/api';
import type { ChangelogEntry } from '@/services/api/changelog';
import usePermissions from '@/hooks/usePermissions';

/**
 * "Das ist neu" (Fix 6, docs/tasks/feedback-fixes-2026-07-23.md).
 *
 * Zeigt nach dem Login einmalig die ungesehenen Changelog-Eintraege
 * (neueste zuerst, Server sortiert). "Verstanden" bestaetigt den hoechsten
 * Eintrag am Server — danach erscheint das Modal nicht wieder. Schliessen
 * ohne "Verstanden" (ESC/Backdrop/X) laesst den Lesestand unangetastet:
 * das Modal kommt beim naechsten Login erneut, blockiert aber nie.
 *
 * Fetch-Fehler bleiben still (kein Modal, kein Toast): die telefonie-Rolle
 * bekommt hier per Deny-by-default einen 403 — gewollt, die Onboarding-
 * Rolle sieht keine CRM-Neuigkeiten.
 *
 * Nicht fuer `cc` und `telefonie` (Entscheid Alex 01.10.2026, Paket 1b N4):
 * die Wizard-only-Rollen sehen den Changelog nicht — hier wird gar nicht erst
 * abgefragt. Serverseitig sind die Endpoints nur fuer sa|vp offen
 * (`Role::CHANGELOG_ROLES`).
 *
 * Abgrenzung: der Versions-Banner ("App neu laden") ist ein ANDERES
 * Instrument und bleibt unberuehrt.
 */
const ChangelogModal: FC = () => {
  const { isWizardOnlyRole } = usePermissions();
  const ohneChangelog = isWizardOnlyRole();
  const [entries, setEntries] = useState<ChangelogEntry[]>([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (ohneChangelog) return undefined;
    let mounted = true;
    api.changelog
      .getUnseen()
      .then((res) => {
        if (mounted && res.data.length > 0) {
          setEntries(res.data);
          setOpen(true);
        }
      })
      .catch(() => {
        // still: telefonie-403 oder Netzfehler duerfen den Login-Flow nie stoeren
      });
    return () => {
      mounted = false;
    };
  }, [ohneChangelog]);

  const handleVerstanden = useCallback(async () => {
    if (entries.length === 0) return;
    setSaving(true);
    try {
      // Server liefert neueste zuerst — der hoechste Stand ist die groesste id.
      await api.changelog.markSeen(Math.max(...entries.map((e) => e.id)));
    } catch {
      // Bestaetigung fehlgeschlagen -> Modal trotzdem schliessen; es erscheint
      // beim naechsten Login erneut. Kein Fehlerzustand, der den User blockiert.
    } finally {
      setSaving(false);
      setOpen(false);
    }
  }, [entries]);

  if (ohneChangelog || !open) return null;

  return (
    <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>Das ist neu</DialogTitle>
      <DialogContent>
        <Stack gap={4}>
          {entries.map((entry) => (
            <Stack key={entry.id} gap={1}>
              <Typography variant="kartentitel" component="p">{entry.title}</Typography>
              <Typography variant="wert" sx={{ whiteSpace: 'pre-line' }}>
                {entry.body}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <CTAButton label="Verstanden" variant="secondary" onClick={handleVerstanden} disabled={saving} loading={saving} />
      </DialogActions>
    </Dialog>
  );
};

export default ChangelogModal;
